import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { MdClose } from "react-icons/md";
import { ReactNode, useCallback } from "react";

export const MODAL_PAPER_MOBILE =
  "!flex !flex-col !h-full !overflow-x-hidden !shadow-card app-card";

export const MODAL_PAPER_DESKTOP_BASE =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !max-h-[90vh] !w-full !max-w-[calc(100vw-2rem)] !overflow-x-hidden";

const MAX_WIDTH_CLASS: Record<"sm" | "md" | "lg", string> = {
  sm: "sm:!max-w-md",
  md: "sm:!max-w-3xl",
  lg: "sm:!max-w-5xl",
};

const CLOSE_BUTTON_CLASS =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100";

export interface ModalCloseButtonProps {
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
}

/** Small top-corner close (X) used by ValutDialog and wizard modals. */
export function ModalCloseButton({
  onClick,
  className = "",
  disabled = false,
  "aria-label": ariaLabel = "Fechar",
}: ModalCloseButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={`${CLOSE_BUTTON_CLASS} ${className}`.trim()}
    >
      <MdClose className="h-5 w-5" aria-hidden />
    </button>
  );
}

export interface ValutDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: "sm" | "md" | "lg";
  titleId?: string;
  bodyClassName?: string;
  disableBackdropClose?: boolean;
  hideCloseButton?: boolean;
}

export function ValutDialog({
  open,
  onClose,
  title,
  icon,
  children,
  footer,
  maxWidth = "md",
  titleId,
  bodyClassName = "",
  disableBackdropClose = false,
  hideCloseButton = false,
}: ValutDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = useCallback(
    (_: unknown, reason?: string) => {
      if (
        disableBackdropClose &&
        (reason === "backdropClick" || reason === "escapeKeyDown")
      ) {
        return;
      }
      onClose();
    },
    [disableBackdropClose, onClose]
  );

  const paperClass = isMobile
    ? MODAL_PAPER_MOBILE
    : `${MODAL_PAPER_DESKTOP_BASE} ${MAX_WIDTH_CLASS[maxWidth]}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={maxWidth}
      scroll="paper"
      PaperProps={{ className: paperClass }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800 sm:px-6">
          <h2
            id={titleId}
            className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100"
          >
            {icon ? (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-[#e7a73f] dark:bg-gray-800">
                {icon}
              </span>
            ) : null}
            {title}
          </h2>
          {!hideCloseButton ? <ModalCloseButton onClick={onClose} /> : null}
        </header>

        <div
          className={`min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 ${!isMobile ? "max-h-[70vh]" : ""} ${bodyClassName}`.trim()}
        >
          {children}
        </div>

        {footer ? (
          <footer className="mt-auto flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-white px-4 pb-5 pt-4 dark:border-gray-800 dark:bg-brand-dark-surface sm:flex-row sm:justify-end sm:px-6">
            {footer}
          </footer>
        ) : null}
      </div>
    </Dialog>
  );
}
